"use client";

import { useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import { Add, Remove, ShoppingCart } from "@mui/icons-material";
import { Button, Box, TextField, Grid } from "@mui/material";
import { FlexBox } from '@/components/flex-box';
import useCart from "hooks/useCart";

export default function AddToCart({ product, compact = false }) {
  const { id, price, discount_price, name, slug, photo, vendor } = product;
  const { addToCart, decreaseFromCart, removeFromCart, state } = useCart();

  const [isLoading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const increment = () => setQuantity((prev) => prev + 1);
  const decrement = () => setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  const handleAddToCart = async () => {
    try {
      setLoading(true);
      const pricef = discount_price === 0 ? price : discount_price;

      // 🔹 Llamamos al método del contexto (maneja localStorage + backend)
      await addToCart({
        id,
        slug,
        price: pricef,
        name,
        photo,
        qty: quantity,
        vendor,
      }, quantity);
    } catch (error) {
      console.error("❌ Error al agregar al carrito:", error);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Versión compacta (solo icono de carrito)
  if (compact) {
    return (
      <LoadingButton
        size="small"
        color="primary"
        variant="contained"
        loading={isLoading}
        onClick={handleAddToCart}
        sx={{
          minWidth: 32,
          width: 32,
          height: 32,
          borderRadius: "50%",
          p: 0,
          boxShadow: 1,
        }}
      >
        <ShoppingCart fontSize="small" />
      </LoadingButton>
    );
  }

  // 🔹 Versión normal
  return (
    <Grid container alignItems="center" spacing={0.5}>
      <Grid item xs={6}>
        <FlexBox alignItems="center" justifyContent="center" gap={0.5}>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            disabled={quantity === 1}
            onClick={decrement}
            sx={{
              height: 28,
              width: 28,
              minWidth: 28,
              borderRadius: 1,
              padding: 0,
            }}
          >
            <Remove fontSize="inherit" />
          </Button>

          <TextField
            type="number"
            value={quantity}
            onChange={(e) => {
              let value = e.target.value;
              if (value.length > 1 && value.startsWith("0")) {
                value = value.replace(/^0+/, "");
              }
              const numValue = value ? Number(value) : "";
              if (numValue === "" || numValue >= 1) {
                setQuantity(numValue);
              }
            }}
            onBlur={() => {
              if (!quantity || quantity < 1) setQuantity(1);
            }}
            inputProps={{
              min: 1,
              style: { textAlign: "center", padding: "4px 0", fontSize: 13 },
            }}
            variant="outlined"
            sx={{
              width: 34,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
              "& input": {
                textAlign: "center",
                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                },
                MozAppearance: "textfield",
              },
            }}
          />

          <Button
            size="small"
            color="primary"
            variant="outlined"
            onClick={increment}
            sx={{
              height: 28,
              width: 28,
              minWidth: 28,
              borderRadius: 1,
              padding: 0,
            }}
          >
            <Add fontSize="inherit" />
          </Button>
        </FlexBox>
      </Grid>

      <Grid item xs={6}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <LoadingButton
            color="primary"
            variant="contained"
            loading={isLoading}
            onClick={handleAddToCart}
            sx={{
              minWidth: 32,
              width: 32,
              height: 32,
              borderRadius: "50%",
              padding: 0,
            }}
          >
            <Add fontSize="small" />
          </LoadingButton>
        </Box>
      </Grid>
    </Grid>
  );
}
